import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { authApi } from "../../services/api/authApi";
import { showSuccess, showError } from "../../lib/toast";
import { useAuth } from "../../contexts/AuthContext";
import * as yup from "yup";

interface SignupFormData {
  firstName?: string;
  lastName?: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
  promoCode: string;
  policyAcceptance: {
    noRefundsPolicy: boolean;
    termsOfService: boolean;
    privacyPolicy: boolean;
  };
}

interface SignupFormErrors {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
  promoCode?: string;
  policyAcceptance?: {
    noRefundsPolicy?: string;
    termsOfService?: string;
    privacyPolicy?: string;
  };
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SignupFormData>({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    password: "",
    confirmPassword: "",
    promoCode: "",
    policyAcceptance: {
      noRefundsPolicy: false,
      termsOfService: false,
      privacyPolicy: false,
    },
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<SignupFormErrors>({});
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { signup } = useAuth();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDigivision = queryParams.get("isDigivision") === "true";
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

      if (name === "phone") {
          // Check for invalid characters
          const invalidChars = /[A-Za-z]/.test(value);
          if (invalidChars) {
              setErrors((prev) => ({
                  ...prev,
                  [name]: "Only numbers and optional + at start are allowed",
              }));
              return; // Stop further processing
          }

          // Allow + at start, digits after
          let formattedValue = value.replace(/[^\d+]/g, "");

          // Ensure + only appears at start
          if (formattedValue.indexOf("+") > 0) {
              formattedValue = formattedValue.replace(/\+/g, "");
          }

          setFormData((prev) => ({
              ...prev,
              [name]: formattedValue,
          }));

          // Clear error if input becomes valid again
          setErrors((prev) => ({
              ...prev,
              [name]: undefined,
          }));
          return;
      }

      // PROMO CODE AUTO UPPERCASE
      if (name === "promoCode") {
          setFormData((prev) => ({
              ...prev,
              promoCode: value.toUpperCase(),
          }));

          setErrors((prev) => ({
              ...prev,
              promoCode: undefined,
          }));
          return;
      }

    if (name.startsWith('policyAcceptance.')) {
      const policyName = name.split('.')[1] as keyof typeof formData.policyAcceptance;
      setFormData((prev) => ({
        ...prev,
        policyAcceptance: {
          ...prev.policyAcceptance,
          [policyName]: checked,
        },
      }));
      setErrors((prev) => ({
        ...prev,
        policyAcceptance: {
          ...(prev.policyAcceptance || {}),
          [policyName]: undefined,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
      setErrors((prev) => ({ ...prev, [name]: undefined } as any));
    }
  };

  const signupSchema = yup.object({
    firstName: yup.string().trim().required("First name is required"),
    lastName: yup.string().trim().required("Last name is required"),
    phone: yup
      .string()
      .required("Phone number is required")
      // .matches(/^\+?\d{10,15}$/, "Enter a valid phone number (10–15 digits)"),
        .test("is-valid-phone", "Enter a valid phone number (10–15 digits)", (val) => {
            if (!val) return true;
            const digits = val.replace(/\D/g, "");
            return digits.length === 0 || (digits.length >= 10 && digits.length <= 15);
        }),
    email: yup.string().required("Email is required").email("Email is invalid"),
    password: yup
      .string()
      .required("Password is required")
      .min(6, "Password must be at least 6 characters"),
    confirmPassword: yup
      .string()
      .required("Confirm password is required")
      .oneOf([yup.ref("password")], "Passwords do not match"),
    promoCode: yup
        .string()
        .optional()
        .matches(/^[A-Z0-9-]*$/, "Promo code must contain only uppercase letters and numbers and hyphens"),
      policyAcceptance: yup.object({
      noRefundsPolicy: yup.boolean().oneOf([true], "You must accept the No Refunds Policy"),
      termsOfService: yup.boolean().oneOf([true], "You must accept the Terms of Service"),
      privacyPolicy: yup.boolean().oneOf([true], "You must accept the Privacy Policy"),
    }),
  });

  const validateForm = async (): Promise<boolean> => {
    try {
      // ✅ Validate all fields and collect all errors (abortEarly: false)
      await signupSchema.validate(formData, { abortEarly: false });

      // ✅ No validation errors
      setErrors({});
      return true;
    } catch (err: any) {
      const fieldErrors = {} as SignupFormErrors;
  
      // ✅ Yup returns an array of validation errors inside `err.inner`
      if (Array.isArray(err.inner) && err.inner.length > 0) {
        err.inner.forEach((errorItem: any) => {
          if (!errorItem.path || !errorItem.message) return;
  
          // ✅ Handle nested policyAcceptance.* keys properly
          if (errorItem.path.startsWith("policyAcceptance.")) {
            const key = errorItem.path.split(".")[1] as keyof NonNullable<SignupFormErrors["policyAcceptance"]>;
            fieldErrors.policyAcceptance = {
              ...(fieldErrors.policyAcceptance ?? {}),
              [key]: errorItem.message,
            };
          } else {
            (fieldErrors as any)[errorItem.path] = errorItem.message;
          }
        });
      }
      // ✅ Fallback if Yup doesn't use `.inner`
      else if (err.path && err.message) {
        (fieldErrors as any)[err.path] = err.message;
          showError(err.message);
      }
      // ✅ Update form errors state
      setErrors(fieldErrors);
  
      // ✅ Show first validation error message to user
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log("handleSubmit ");
    e.preventDefault();

    const isValid = await validateForm();
    if (!isValid) return;

    setIsSubmitting(true);

    try {
      const { confirmPassword, ...registrationData } = formData;

      const response = await authApi.register(registrationData);
      console.log("Registration response:", response);

      // Check if response has the expected user data
      if (response?.success && response?.data?.userId) {
        // Update auth context with signup state
        signup({
          id: response.data.userId,
          role: "user", // Default role
          ...formData,
        });

        const successMessage = response.message || "Registration successful! Please check your email to verify your account. The verification link will expire in 30 minutes.";
        showSuccess(successMessage);
        navigate("/login");
      } else {
        showError("Unexpected response from server. Please try again.");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      showError(errorMessage);

      // Handle specific error cases
      if (error.response?.data?.errors) {
          setErrors({promoCode:error.response.data.error});
              setErrors(error.response.data.errors);
          }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column">
      {/* Breadcrumb */}
      <div className="py-3">
        <div className="container">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 font_two">
              <li className="breadcrumb-item">
                <a href="/" className="text-decoration-none text-dark">
                  Home
                </a>
              </li>
              <li
                className="breadcrumb-item active fw-bold"
                aria-current="page"
              >
                Sign Up
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Sign Up Section */}
      <div className="container flex-grow-1 d-flex flex-column flex-md-row py-1">
        {/* Left Content */}
        <div className="col-md-6 mb-2">
          <h4 className="mb-3 display-6">Sign Up</h4>
          <p className="font_two text-muted mb-3">
            Your profile is where your journey with art begins.
          </p>
          <a href="/login" className="font_two text-link">
            Existing user? Login
          </a>
        </div>

        {/* Right Form */}
        <div className="col-md-6">
          <form
            onSubmit={handleSubmit}
            className="bg-white p-10 p-md-3 rounded-3 shadow-sm mb-3"
          >
            <div className="row">
              <div className="col-md-6">
                <label htmlFor="firstName" className="form-label fw-medium">
                  First Name <span className="text-danger">*</span>
                </label>
                <input
                  placeholder="Enter First Name"
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`form-control form-control-sm custom-input ${errors.firstName ? "is-invalid" : ""}`}
                  // required
                />
                {errors.firstName && (
                  <div className="font_two invalid-feedback">{errors.firstName}</div>
                )}
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label fw-medium">
                  Last Name <span className="text-danger">*</span>
                </label>
                <input
                  placeholder="Enter Last Name"
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`form-control form-control-sm custom-input ${errors.lastName ? "is-invalid" : ""}`}
                  // required
                />
                {errors.lastName && (
                  <div className="font_two invalid-feedback">{errors.lastName}</div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label fw-medium">
                Email <span className="text-danger">*</span>
              </label>
              <input
                placeholder="Enter Email Address"
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`form-control form-control-sm custom-input ${errors.email ? "is-invalid" : ""}`}
                // required
              />
              {errors.email && (
                <div className="font_two invalid-feedback">{errors.email}</div>
              )}
            </div>
            <div className="mb-3">
              <label htmlFor="phone" className="form-label fw-medium">
                Phone Number <span className="text-danger">*</span>
              </label>
              <input
                placeholder="Enter Phone Number"
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`form-control form-control-sm custom-input ${errors.phone ? "is-invalid" : ""}`}
              />
              {errors.phone && (
                <div className="font_two invalid-feedback">{errors.phone}</div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="password" className="form-label">
                Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                    type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`form-control form-control-sm custom-input ${errors.password ? "is-invalid" : ""}`}
                  // required
                />

                  <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                  >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                  </button>

                {errors.password && (
                  <div className="font_two invalid-feedback d-block">
                    {errors.password}
                  </div>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="confirmPassword" className="form-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <div className="input-group">
                <input
                    type={showConfirmPassword ? "text" : "password"}
                  placeholder="Enter Confirm Password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`form-control form-control-sm custom-input ${errors.confirmPassword ? "is-invalid" : ""}`}
                  // required
                />

                  <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                      {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                  </button>

                {errors.confirmPassword && (
                  <div className="font_two invalid-feedback d-block">
                    {errors.confirmPassword}
                  </div>
                )}
              </div>
            </div>

            {isDigivision && <div className="mb-3">
              <label htmlFor="phone" className="form-label fw-medium">
                Promo Code
              </label>
              <input
                placeholder="Enter Promo Code"
                type="text"
                id="promoCode"
                name="promoCode"
                value={formData.promoCode}
                onChange={handleChange}
                className={`form-control form-control-sm custom-input ${
                    errors.promoCode ? "is-invalid" : ""
                }`}
              />

                {errors.promoCode && (
                    <div className="font_two invalid-feedback d-block">
                        {errors.promoCode}
                    </div>
                )}
            </div>}

            {/* Policy Acceptance Section */}
            <div className="mb-4">
              <h6 className="mb-3 fw-medium">Terms & Policies</h6>

              <div className="Checkout_model_info_area_checking form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="noRefundsPolicy"
                  name="policyAcceptance.noRefundsPolicy"
                  checked={formData.policyAcceptance.noRefundsPolicy}
                  onChange={handleChange}
                  style={{ accentColor: "#000" }}
                  // required
                />
                <label className="font_two form-check-label small" htmlFor="noRefundsPolicy">
                  I accept the <a href="/terms-and-conditions" target="_blank" className="text-link">No Refunds Policy</a> <span className="text-danger">*</span>
                </label>
                {errors.policyAcceptance?.noRefundsPolicy && (
                  <div className="font_two text-danger small mt-1">{errors.policyAcceptance.noRefundsPolicy}</div>
                )}
              </div>

              <div className="Checkout_model_info_area_checking form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="termsOfService"
                  name="policyAcceptance.termsOfService"
                  checked={formData.policyAcceptance.termsOfService}
                  onChange={handleChange}
                  // required
                />
                <label className="font_two form-check-label small" htmlFor="termsOfService">
                  I accept the <a href="/terms" target="_blank" className="text-link">Terms of Service</a> <span className="text-danger">*</span>
                </label>
                {errors.policyAcceptance?.termsOfService && (
                  <div className="font_two text-danger small mt-1">{errors.policyAcceptance.termsOfService}</div>
                )}
              </div>

              <div className="Checkout_model_info_area_checking form-check mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="privacyPolicy"
                  name="policyAcceptance.privacyPolicy"
                  checked={formData.policyAcceptance.privacyPolicy}
                  onChange={handleChange}
                  // required
                />
                <label className="font_two form-check-label small" htmlFor="privacyPolicy">
                  I accept the <a href="/privacy" target="_blank" className="text-link">Privacy Policy</a> <span className="text-danger">*</span>
                </label>
                {errors.policyAcceptance?.privacyPolicy && (
                  <div className="font_two text-danger small mt-1">{errors.policyAcceptance.privacyPolicy}</div>
                )}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-dark primary-btn w-50 rounded-pill mt-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Signing Up...
                </>
              ) : (
                "Sign Up"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Signup;
